import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { appDate } from '../../../../utils/appDateFormat';

interface Employee {
  id: string;
  name: string;
  employeeId: string;
}

interface Calculation {
  totalAmount: number;
  gratuity?: {
    adjustedAmount: number;
  };
  leaveEncashment?: number;
  pendingDeductions?: number;
}

interface PayrollCycle {
  id: string;
  name: string;
  status: string;
  cutoffDate: string;
  paymentDate: string;
  isDefault: boolean;
}

interface PayrollOptions {
  payrollCycle: string;
  paymentMethod: string;
  taxTreatment: string;
  approvalRequired: boolean;
  notifyEmployee: boolean;
  paymentCategory: string;
  processingNotes: string;
}

interface PayrollData {
  employee: Employee;
  calculation: Calculation;
  payrollOptions: PayrollOptions;
  addedAt: string;
  payrollEntryId: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee;
  calculation: Calculation;
  onPayrollAdded: (data: PayrollData) => void;
}

export default function PayrollIntegrationModal({ isOpen, onClose, employee, calculation, onPayrollAdded }: Props) {
  const { t } = useTranslation();
  const [payrollOptions, setPayrollOptions] = useState<PayrollOptions>({
    payrollCycle: '',
    paymentMethod: 'bank_transfer',
    taxTreatment: 'standard',
    approvalRequired: true,
    notifyEmployee: true,
    paymentCategory: 'termination_benefits',
    processingNotes: '',
  });
  const [payrollCycles, setPayrollCycles] = useState<PayrollCycle[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Mock payroll cycles data
  useEffect(() => {
    if (isOpen) {
      const mockCycles: PayrollCycle[] = [
        {
          id: 'DEC_2024',
          name: t('December 2024'),
          status: 'Open',
          cutoffDate: '2024-12-25',
          paymentDate: '2024-12-31',
          isDefault: true,
        },
        {
          id: 'JAN_2025',
          name: t('January 2025'),
          status: 'Draft',
          cutoffDate: '2025-01-25',
          paymentDate: '2025-01-31',
          isDefault: false,
        },
        {
          id: 'FEB_2025',
          name: t('February 2025'),
          status: 'Draft',
          cutoffDate: '2025-02-25',
          paymentDate: '2025-02-28',
          isDefault: false,
        },
      ];
      setPayrollCycles(mockCycles);

      // Set default payroll cycle
      const defaultCycle = mockCycles.find((cycle) => cycle.isDefault);
      if (defaultCycle) {
        setPayrollOptions((prev) => ({ ...prev, payrollCycle: defaultCycle.id }));
      }
    }
  }, [isOpen, t]);

  if (!isOpen) return null;

  const formatCurrency = (amount: number | undefined) => {
    return `AED ${amount?.toLocaleString() || '0.00'}`;
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!payrollOptions.payrollCycle) {
      errors.payrollCycle = t('Please select a payroll cycle');
    }

    if (!payrollOptions.paymentMethod) {
      errors.paymentMethod = t('Please select a payment method');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOptionChange = (key: keyof PayrollOptions, value: string | boolean) => {
    setPayrollOptions((prev) => ({ ...prev, [key]: value }));
    // Clear validation error when user makes a change
    if (validationErrors[key]) {
      setValidationErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const handleAddToPayroll = async () => {
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    // Simulate payroll integration
    setTimeout(() => {
      const payrollData: PayrollData = {
        employee,
        calculation,
        payrollOptions,
        addedAt: new Date().toISOString(),
        payrollEntryId: `PAYROLL-EOS-${employee.employeeId}-${Date.now()}`,
      };

      onPayrollAdded(payrollData);
      setIsProcessing(false);
    }, 2000);
  };

  const selectedCycle = payrollCycles.find((cycle) => cycle.id === payrollOptions.payrollCycle);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Plus className="w-6 h-6 text-emerald-600" />
            <div>
              <h2 className="text-xl font-bold text-slate-900">{t('Add EOS to Payroll')}</h2>
              <p className="text-slate-600">{t('Integrate End of Service payment into payroll cycle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Payment Summary */}
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6 mb-6 border border-emerald-200">
            <h3 className="font-semibold text-emerald-900 mb-4">{t('Payment Summary')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-emerald-700 text-sm font-medium">{t('Employee')}</p>
                <p className="text-lg font-bold text-emerald-900">{employee.name}</p>
              </div>
              <div>
                <p className="text-emerald-700 text-sm font-medium">{t('Employee ID')}</p>
                <p className="text-lg font-bold text-emerald-900">{employee.employeeId}</p>
              </div>
              <div>
                <p className="text-emerald-700 text-sm font-medium">{t('Total EOS Amount')}</p>
                <p className="text-2xl font-bold text-emerald-900">{formatCurrency(calculation.totalAmount)}</p>
              </div>
              <div>
                <p className="text-emerald-700 text-sm font-medium">{t('Payment Breakdown')}</p>
                <div className="text-sm text-emerald-800">
                  <p>
                    {t('Gratuity')}: {formatCurrency(calculation.gratuity?.adjustedAmount)}
                  </p>
                  <p>
                    {t('Leave')}: {formatCurrency(calculation.leaveEncashment)}
                  </p>
                  <p>
                    {t('Deductions')}: -{formatCurrency(calculation.pendingDeductions)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payroll Configuration */}
          <div className="space-y-6">
            {/* Payroll Cycle Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('Payroll Cycle')} *
              </label>
              <select
                value={payrollOptions.payrollCycle}
                onChange={(e) => handleOptionChange('payrollCycle', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                  validationErrors.payrollCycle ? 'border-red-500' : 'border-border'
                }`}
              >
                <option value="">{t('Select payroll cycle')}</option>
                {payrollCycles.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name} - {cycle.status}
                    {cycle.isDefault ? ` (${t('Default')})` : ''}
                  </option>
                ))}
              </select>
              {validationErrors.payrollCycle && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.payrollCycle}</p>
              )}
            </div>

            {/* Payroll Cycle Details */}
            {selectedCycle && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-3">{t('Selected Cycle Details')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-blue-700 font-medium">{t('Cycle Status')}</p>
                    <p className="text-blue-900">{selectedCycle.status}</p>
                  </div>
                  <div>
                    <p className="text-blue-700 font-medium">{t('Cutoff Date')}</p>
                    <p className="text-blue-900">{appDate(selectedCycle.cutoffDate)}</p>
                  </div>
                  <div>
                    <p className="text-blue-700 font-medium">{t('Payment Date')}</p>
                    <p className="text-blue-900">{appDate(selectedCycle.paymentDate)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('Payment Method')} *
              </label>
              <select
                value={payrollOptions.paymentMethod}
                onChange={(e) => handleOptionChange('paymentMethod', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                  validationErrors.paymentMethod ? 'border-red-500' : 'border-border'
                }`}
              >
                <option value="bank_transfer">{t('Bank Transfer')}</option>
                <option value="check">{t('Check Payment')}</option>
                <option value="cash">{t('Cash Payment')}</option>
                <option value="direct_deposit">{t('Direct Deposit')}</option>
              </select>
              {validationErrors.paymentMethod && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.paymentMethod}</p>
              )}
            </div>

            {/* Payment Category */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('Payment Category')}</label>
              <select
                value={payrollOptions.paymentCategory}
                onChange={(e) => handleOptionChange('paymentCategory', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="termination_benefits">{t('Termination Benefits')}</option>
                <option value="severance_pay">{t('Severance Pay')}</option>
                <option value="end_of_service">{t('End of Service Gratuity')}</option>
                <option value="other_benefits">{t('Other Benefits')}</option>
              </select>
            </div>

            {/* Tax Treatment */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('Tax Treatment')}</label>
              <select
                value={payrollOptions.taxTreatment}
                onChange={(e) => handleOptionChange('taxTreatment', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="standard">{t('Standard Tax Treatment')}</option>
                <option value="tax_exempt">{t('Tax Exempt')}</option>
                <option value="reduced_tax">{t('Reduced Tax Rate')}</option>
                <option value="manual_calculation">{t('Manual Tax Calculation')}</option>
              </select>
            </div>

            {/* Processing Options */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">{t('Processing Options')}</h4>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={payrollOptions.approvalRequired}
                  onChange={(e) => handleOptionChange('approvalRequired', e.target.checked)}
                  className="rounded border-border text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-slate-700">{t('Require approval before processing')}</span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={payrollOptions.notifyEmployee}
                  onChange={(e) => handleOptionChange('notifyEmployee', e.target.checked)}
                  className="rounded border-border text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-slate-700">{t('Send notification to employee')}</span>
              </label>
            </div>

            {/* Processing Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('Processing Notes')}</label>
              <textarea
                rows={3}
                placeholder={t('Add any special instructions or notes for payroll processing...')}
                value={payrollOptions.processingNotes}
                onChange={(e) => handleOptionChange('processingNotes', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Integration Summary */}
          <div className="mt-6 bg-amber-50 rounded-xl p-6 border border-amber-200">
            <h3 className="font-semibold text-amber-900 mb-4">{t('Integration Summary')}</h3>
            <div className="text-sm text-amber-800 space-y-2">
              <p>• {t('EOS payment will be added to the selected payroll cycle')}</p>
              <p>• {t('Payment will appear as a separate line item in payroll processing')}</p>
              <p>• {t('Tax calculations will be applied based on selected treatment')}</p>
              <p>• {t('Employee will receive payment notification if enabled')}</p>
              {payrollOptions.approvalRequired && (
                <p>• {t('Payment requires approval before final processing')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border bg-muted">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              {t('Payment will be integrated into the selected payroll cycle')}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="px-4 py-2 border border-border rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleAddToPayroll}
                disabled={isProcessing}
                className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                <span>{isProcessing ? t('Adding to Payroll...') : t('Add to Payroll')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
